<script>
/**
 * CONTEXTO do cartão flutuante do painel — só dado real (`utils/contexto-do-painel.js`):
 * cliente em foco (ou "Escritório inteiro"), agente(s) do turno e as fontes
 * consultadas (pílula cinza: rótulo + registros). As fontes ocupam UMA linha:
 * uma fileira oculta (`medidor`) mede cada pílula e o "+N" mais largo possível,
 * `pilulasQueCabem` decide quantas entram e o resto soma no "+N" do fim (com as
 * `fontesExcedentes`). Remede no `ResizeObserver` da linha. Sem "sincronizado
 * há X min" (não temos esse dado) e sem chips de filtro (o getter opcional
 * `filtrosDaTela` não existe neste store).
 */
import { PAINEL_DO_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { formatNumber } from '@utils/Format'
import { pilulasQueCabem } from '@modules/aawz-intelligence-v2/utils/pilulas-que-cabem'

// O `az-gap-xsmall` entre pílulas, para a conta do que cabe.
const GAP_ENTRE_PILULAS = 4

export default {
  name: 'IntelligenceV2PainelSecaoContexto',
  props: {
    contexto: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      copy: PAINEL_DO_CHAT,
      larguraDisponivel: 0,
      largurasDasPilulas: [],
      larguraDoMais: 0,
    }
  },
  computed: {
    agentes() {
      return (this.contexto.agentes || []).join(', ')
    },
    fontes() {
      return this.contexto.fontes || []
    },
    // Registros com separador de milhar ("1.498").
    fontesFormatadas() {
      return this.fontes.map(function (fonte) {
        return { ...fonte, registros: formatNumber(fonte.registros, 0) }
      })
    },
    excedentes() {
      return this.contexto.fontesExcedentes || 0
    },
    // Medida de outra lista de fontes (ou nenhuma ainda): mostra todas até remedir.
    quantasCabem() {
      if (this.largurasDasPilulas.length !== this.fontes.length) return this.fontes.length
      return pilulasQueCabem({
        larguras: this.largurasDasPilulas,
        disponivel: this.larguraDisponivel,
        gap: GAP_ENTRE_PILULAS,
        larguraDoMais: this.larguraDoMais,
        excedentes: this.excedentes,
      })
    },
    fontesVisiveis() {
      return this.fontesFormatadas.slice(0, this.quantasCabem)
    },
    maisFontes() {
      const ocultas = this.fontes.length - this.quantasCabem + this.excedentes
      return ocultas > 0 ? PAINEL_DO_CHAT.MAIS_FONTES(ocultas) : ''
    },
    // O "+N" mais largo que pode aparecer, para reservar espaço.
    maiorMaisFontes() {
      return PAINEL_DO_CHAT.MAIS_FONTES(this.fontes.length + this.excedentes)
    },
  },
  watch: {
    fontes() {
      this.$nextTick(this.medirFontes)
    },
  },
  mounted() {
    this.$nextTick(this.medirFontes)
    if (typeof ResizeObserver === 'undefined') return
    this.observadorDaLinha = new ResizeObserver(() => this.medirFontes())
    if (this.$refs.linhaDeFontes) this.observadorDaLinha.observe(this.$refs.linhaDeFontes)
  },
  beforeDestroy() {
    if (this.observadorDaLinha) this.observadorDaLinha.disconnect()
  },
  methods: {
    medirFontes() {
      const medidor = this.$refs.medidor
      const linha = this.$refs.linhaDeFontes
      if (!medidor || !linha) return
      const pilulas = medidor.querySelectorAll('[data-medida="pilula"]')
      this.largurasDasPilulas = Array.from(pilulas).map((el) => el.getBoundingClientRect().width)
      const mais = medidor.querySelector('[data-medida="mais"]')
      this.larguraDoMais = mais ? mais.getBoundingClientRect().width : 0
      this.larguraDisponivel = linha.clientWidth
    },
  },
}
</script>

<template>
  <section class="d-flex flex-column az-gap-small" data-test="painel-secao-contexto">
    <h3 class="az-text-primary az-size-14 font-weight-medium ma-0 mb-1">{{ copy.CONTEXTO }}</h3>
    <dl class="d-flex flex-column ma-0 painel-secao-contexto__lista">
      <div class="painel-secao-contexto__linha d-flex align-baseline az-gap-small">
        <dt class="painel-secao-contexto__chave ia-sidebar-aux flex-shrink-0">{{ copy.CLIENTE }}</dt>
        <dd
          class="az-text-primary az-size-12 text-truncate ma-0"
          :title="contexto.cliente"
          data-test="painel-secao-contexto-cliente"
        >{{ contexto.cliente }}</dd>
      </div>
      <div v-if="agentes" class="painel-secao-contexto__linha d-flex align-baseline az-gap-small">
        <dt class="painel-secao-contexto__chave ia-sidebar-aux flex-shrink-0">{{ copy.AGENTE }}</dt>
        <dd
          class="az-text-primary az-size-12 text-truncate ma-0"
          :title="agentes"
          data-test="painel-secao-contexto-agentes"
        >{{ agentes }}</dd>
      </div>
      <div v-if="fontes.length" class="painel-secao-contexto__linha d-flex align-start az-gap-small">
        <dt class="painel-secao-contexto__chave ia-sidebar-aux flex-shrink-0 pt-1">{{ copy.FONTES }}</dt>
        <dd
          ref="linhaDeFontes"
          class="painel-secao-contexto__fontes d-flex flex-nowrap az-gap-xsmall ma-0"
          data-test="painel-secao-contexto-fontes"
        >
          <span
            v-for="fonte in fontesVisiveis"
            :key="fonte.tabela"
            class="painel-secao-contexto__pilula d-inline-flex align-center az-gap-xsmall"
            :title="fonte.rotulo"
            data-test="painel-secao-contexto-fonte"
          >
            <span class="painel-secao-contexto__rotulo az-text-primary az-size-12 text-truncate">{{ fonte.rotulo }}</span>
            <span class="ia-sidebar-aux flex-shrink-0">{{ fonte.registros }}</span>
          </span>
          <span
            v-if="maisFontes"
            class="painel-secao-contexto__pilula ia-sidebar-aux d-inline-flex align-center flex-shrink-0"
            data-test="painel-secao-contexto-mais-fontes"
          >{{ maisFontes }}</span>
          <span ref="medidor" class="painel-secao-contexto__medidor d-flex az-gap-xsmall" aria-hidden="true">
            <span
              v-for="fonte in fontesFormatadas"
              :key="fonte.tabela"
              class="painel-secao-contexto__pilula d-inline-flex align-center az-gap-xsmall"
              data-medida="pilula"
            >
              <span class="az-text-primary az-size-12">{{ fonte.rotulo }}</span>
              <span class="ia-sidebar-aux">{{ fonte.registros }}</span>
            </span>
            <span class="painel-secao-contexto__pilula ia-sidebar-aux d-inline-flex" data-medida="mais">{{ maiorMaisFontes }}</span>
          </span>
        </dd>
      </div>
    </dl>
  </section>
</template>

<style lang="sass" scoped>
// Pares chave → valor em linha (rótulo muted numa coluna fixa), fontes como
// pílulas cinzas — antes eram rótulo e valor empilhados, com bolinhas primary.
$coluna-da-chave: 56px

.painel-secao-contexto__lista
  gap: 6px

.painel-secao-contexto__linha
  min-width: 0

  .text-truncate
    min-width: 0

.painel-secao-contexto__chave
  width: $coluna-da-chave

.painel-secao-contexto__fontes
  position: relative
  min-width: 0
  flex: 1
  overflow: hidden

// Fileira fora da tela que mede as pílulas no tamanho natural.
.painel-secao-contexto__medidor
  position: absolute
  top: 0
  left: 0
  visibility: hidden
  pointer-events: none
  white-space: nowrap

  .painel-secao-contexto__pilula
    max-width: none

// Fonte numa linha só: o rótulo corta com "…" (o nome inteiro fica no `title`).
.painel-secao-contexto__pilula
  max-width: 100%
  min-width: 0
  white-space: nowrap
  padding: 2px 8px
  border-radius: var(--radius-sm)
  background: var(--neutral-100)
</style>
